import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import { isFreePlan } from '../../utils/planAccess';
import UpgradeModal from '../../components/UpgradeModal';
import SearchSelect from '../../components/SearchSelect';

// "Masa - 3" → { prefix: 'Masa', suffix: 3 }; "Bar" → { prefix: 'Bar', suffix: null }
const splitName = (nm) => {
    const m = String(nm || '').match(/^(.*?)\s*-\s*(\d+)\s*$/);
    if (m) return { prefix: m[1].trim(), suffix: parseInt(m[2], 10) };
    return { prefix: String(nm || '').trim(), suffix: null };
};

// Qrup combobox — mövcud qruplar dropdown-da açılır (native datalist deyil), yeni də yazıla bilir
function PrefixCombo({ value, onChange, options = [], placeholder = 'Masa' }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);
    return (
        <div ref={ref} className="relative">
            <div className="relative">
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => setOpen(true)}
                    placeholder={placeholder}
                    className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 pr-9 text-sm focus:border-primary-500 focus:outline-none"
                />
                <button type="button" tabIndex={-1} onClick={() => setOpen((o) => !o)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">▾</button>
            </div>
            {open && options.length > 0 && (
                <div className="absolute z-40 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                    {options.map((o) => (
                        <button key={o} type="button"
                            onClick={() => { onChange(o); setOpen(false); }}
                            className={`block w-full truncate px-3 py-2 text-left text-sm border-b border-gray-50 last:border-0 hover:bg-primary-50 ${String(o) === String(value) ? 'bg-primary-50 font-semibold text-primary-700' : 'text-gray-700'}`}>
                            {o}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

const Tables = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const isPro = user?.tenant_plan === 'pro';

    const [tables, setTables] = useState([]);
    const [floorPlan, setFloorPlan] = useState(false); // superadmin «Zal planı» tick-i (PRO)
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterRoom, setFilterRoom] = useState('all');
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeReason, setUpgradeReason] = useState('tables');
    const [deleteCandidate, setDeleteCandidate] = useState(null);

    // Yarat (tək + toplu birləşdirilmiş)
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ prefix: 'Masa', count: 1, name: '', room_id: '', deposit_amount: '0' });
    const [createSaving, setCreateSaving] = useState(false);

    // Redaktə
    const [editTable, setEditTable] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', room_id: '', deposit_amount: '0' });
    const [editSaving, setEditSaving] = useState(false);

    // Sırala
    const [sortOpen, setSortOpen] = useState(false);
    const [groupOrder, setGroupOrder] = useState([]);
    const [sortSaving, setSortSaving] = useState(false);

    useEffect(() => { loadAll(); }, []);

    const loadAll = async () => {
        try {
            const [tabRes, roomRes] = await Promise.all([
                api.get('/tables/all'),
                api.get('/rooms/all')
            ]);
            setTables(tabRes.data.data);
            setFloorPlan(!!tabRes.data.floor_plan);
            setRooms(roomRes.data.data.filter((room) => room.active));
        } catch {
            toast.error('Məlumat yüklənmədi.');
        } finally {
            setLoading(false);
        }
    };

    // Mövcud prefikslər (qruplar) — datalist üçün
    const prefixList = [...new Set(tables.map((t) => splitName(t.name).prefix).filter(Boolean))];
    const nextSuffixFor = (pref) => {
        const p = String(pref || '').trim().toLowerCase();
        let mx = 0;
        tables.forEach((t) => {
            const s = splitName(t.name);
            if (s.prefix.toLowerCase() === p && s.suffix != null && s.suffix > mx) mx = s.suffix;
        });
        return mx + 1;
    };

    // ── Yarat ──
    const openCreate = () => {
        if (isFreePlan(user?.tenant_plan) && tables.length >= 4) {
            setUpgradeReason('tables');
            setShowUpgradeModal(true);
            return;
        }
        const autoRoom = (filterRoom !== 'all' && filterRoom !== 'none') ? filterRoom : '';
        const pref = prefixList[0] || 'Masa';
        setCreateForm({ prefix: pref, count: 1, name: `${pref} - ${nextSuffixFor(pref)}`, room_id: autoRoom, deposit_amount: '0' });
        setCreateOpen(true);
    };

    // Prefiks dəyişəndə adı avtomatik yenilə (növbəti nömrə ilə)
    const setPrefix = (val) => {
        const p = val;
        setCreateForm((f) => ({ ...f, prefix: p, name: `${p.trim()} - ${nextSuffixFor(p)}` }));
    };

    const submitCreate = async () => {
        const cnt = parseInt(createForm.count, 10) || 1;
        const pref = createForm.prefix.trim();
        if (cnt < 1 || cnt > 200) return toast.error('Say 1-200 arasında olmalıdır.');
        if (cnt === 1 && !createForm.name.trim()) return toast.error('Masa adı yazın.');
        if (cnt > 1 && !pref) return toast.error('Qrup adı (prefiks) daxil edin.');
        setCreateSaving(true);
        try {
            const deposit = isPro ? Number(createForm.deposit_amount || 0) : 0;
            const room_id = createForm.room_id || null;
            if (cnt === 1) {
                await api.post('/tables', { name: createForm.name.trim(), room_id, deposit_amount: deposit });
                toast.success('Masa əlavə edildi.');
            } else {
                const res = await api.post('/tables/bulk', { count: cnt, prefix: pref, room_id, deposit_amount: deposit });
                toast.success(res.data.msg || `${res.data.created_count} masa yaradıldı.`);
            }
            setCreateOpen(false);
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setCreateSaving(false);
        }
    };

    // ── Redaktə ──
    const openEdit = (table) => {
        setEditTable(table);
        setEditForm({
            name: table.name,
            room_id: table.room_id || '',
            deposit_amount: String(Number(table.deposit_amount || 0).toFixed(2)),
            count_occupancy: table.count_occupancy == null ? true : Number(table.count_occupancy) === 1,
        });
    };

    const submitEdit = async (e) => {
        e.preventDefault();
        if (!editForm.name.trim()) return toast.error('Masa adı yazın.');
        setEditSaving(true);
        try {
            await api.put(`/tables/${editTable.id}`, {
                name: editForm.name.trim(),
                capacity: editTable.capacity || 4,
                room_id: editForm.room_id || null,
                deposit_amount: isPro ? Number(editForm.deposit_amount || 0) : 0,
                active: editTable.active,
                count_occupancy: editForm.count_occupancy ? 1 : 0,
            });
            toast.success('Masa yeniləndi.');
            setEditTable(null);
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setEditSaving(false);
        }
    };

    const handleToggle = async (table) => {
        try {
            await api.put(`/tables/${table.id}`, {
                name: table.name,
                capacity: table.capacity || 4,
                room_id: table.room_id || null,
                deposit_amount: isPro ? Number(table.deposit_amount || 0) : 0,
                active: table.active ? 0 : 1
            });
            toast.success(table.active ? 'Deaktiv edildi.' : 'Aktiv edildi.');
            loadAll();
        } catch {
            toast.error('Xəta baş verdi.');
        }
    };

    const handleDelete = async () => {
        if (!deleteCandidate) return;
        try {
            await api.delete(`/tables/${deleteCandidate.id}`);
            toast.success('Masa silindi.');
            setDeleteCandidate(null);
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinə bilmədi.');
        }
    };

    // ── Sırala ──
    const openSort = () => {
        const seen = [];
        tables.forEach((t) => {
            const p = splitName(t.name).prefix;
            if (p && !seen.includes(p)) seen.push(p);
        });
        setGroupOrder(seen);
        setSortOpen(true);
    };

    const moveGroup = (idx, dir) => {
        setGroupOrder((prev) => {
            const arr = [...prev];
            const j = idx + dir;
            if (j < 0 || j >= arr.length) return prev;
            [arr[idx], arr[j]] = [arr[j], arr[idx]];
            return arr;
        });
    };

    const saveSort = async () => {
        setSortSaving(true);
        try {
            const ids = [];
            groupOrder.forEach((p) => {
                tables
                    .filter((t) => splitName(t.name).prefix === p)
                    .slice()
                    .sort((a, b) => {
                        const sa = splitName(a.name).suffix, sb = splitName(b.name).suffix;
                        if (sa != null && sb != null) return sa - sb;
                        return a.number - b.number;
                    })
                    .forEach((t) => ids.push(t.id));
            });
            await api.post('/tables/reorder', { ids });
            toast.success('Sıra yadda saxlanıldı.');
            setSortOpen(false);
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setSortSaving(false);
        }
    };

    const handleRoomsClick = () => {
        if (roomAccessLocked) {
            setUpgradeReason('rooms');
            setShowUpgradeModal(true);
            return;
        }
        navigate('/admin/rooms');
    };

    const statusMap = { bos: 'Boş', dolu: 'Dolu', hesab: 'Hesab' };
    const statusColor = {
        bos: 'bg-green-100 text-green-700 border-green-200',
        dolu: 'bg-amber-100 text-amber-700 border-amber-200',
        hesab: 'bg-red-100 text-red-700 border-red-200'
    };

    const filteredTables = (filterRoom === 'all'
        ? tables
        : filterRoom === 'none'
            ? tables.filter((table) => !table.room_id)
            : tables.filter((table) => String(table.room_id) === filterRoom)
    ).slice().sort((a, b) => {
        const aIsTA = !!rooms.find(r => r.id === a.room_id)?.is_takeaway;
        const bIsTA = !!rooms.find(r => r.id === b.room_id)?.is_takeaway;
        if (aIsTA === bIsTA) return 0;
        return aIsTA ? 1 : -1; // takeaway həmişə sona
    });
    const roomAccessLocked = user?.tenant_plan !== 'pro';
    const upgradeModalConfig = upgradeReason === 'rooms'
        ? {
            title: 'Zal / otaqlar Pro paketindədir',
            message: 'Zal, kabinet və otaq idarəsini açmaq üçün planınızı yüksəldin.',
            plans: ['basic', 'pro']
        }
        : {
            title: 'Masa limiti dolub',
            message: 'Pulsuz paketdə maksimum 4 masa dəstəklənir. Daha çox masa üçün planınızı yüksəldin.',
            plans: ['basic', 'pro']
        };

    const inputCls = 'w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none';

    return (
        <Layout>
            <UpgradeModal
                open={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title={upgradeModalConfig.title}
                message={upgradeModalConfig.message}
                plans={upgradeModalConfig.plans}
                currentPlan={user?.tenant_plan}
            />

            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'serif' }}>Masalar</h1>
                    <p className="mt-1 text-sm text-gray-500">{tables.length} masa</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={handleRoomsClick}
                        className="rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 text-sm font-bold text-primary-700 transition-all hover:bg-primary-100"
                    >
                        {roomAccessLocked ? '🔒 🏠 Zal' : '🏠 Zal'}
                    </button>
                    {floorPlan && (
                        <button
                            onClick={() => navigate('/admin/floor-plan')}
                            className="rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 text-sm font-bold text-primary-700 transition-all hover:bg-primary-100"
                        >
                            🗺️ Zal planı
                        </button>
                    )}
                    <button
                        onClick={openSort}
                        disabled={tables.length === 0}
                        className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-600 transition-all hover:border-primary-300 disabled:opacity-50"
                    >
                        ⇅ Sırala
                    </button>
                    <button
                        onClick={openCreate}
                        className="rounded-xl px-3 py-2 text-sm font-bold text-white transition-all hover:-translate-y-0.5"
                        style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                    >
                        + Yeni masa
                    </button>
                </div>
            </div>

            {rooms.length > 0 && (
                <div className="mb-5 flex flex-wrap gap-2">
                    <button
                        onClick={() => setFilterRoom('all')}
                        className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                            filterRoom === 'all' ? 'bg-primary-500 text-white' : 'border border-gray-200 bg-white text-gray-500 hover:border-primary-300'
                        }`}
                    >
                        Hamısı ({tables.length})
                    </button>
                    {rooms.map((room) => (
                        <button
                            key={room.id}
                            onClick={() => setFilterRoom(String(room.id))}
                            className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                                filterRoom === String(room.id) ? 'bg-primary-500 text-white' : 'border border-gray-200 bg-white text-gray-500 hover:border-primary-300'
                            }`}
                        >
                            {room.name} ({tables.filter((table) => String(table.room_id) === String(room.id)).length})
                        </button>
                    ))}
                    <button
                        onClick={() => setFilterRoom('none')}
                        className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                            filterRoom === 'none' ? 'bg-gray-600 text-white' : 'border border-gray-200 bg-white text-gray-500 hover:border-gray-400'
                        }`}
                    >
                        Otaqsız ({tables.filter((table) => !table.room_id).length})
                    </button>
                </div>
            )}

            {loading ? (
                <div className="flex h-48 items-center justify-center text-gray-400">Yüklənir...</div>
            ) : filteredTables.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-white p-12 text-center text-gray-400">
                    {tables.length === 0 ? 'Hələ masa əlavə edilməyib.' : 'Bu otaqda masa yoxdur.'}
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                    {filteredTables.map((table) => (
                        <div
                            key={table.id}
                            className={`rounded-xl border-2 bg-white p-4 transition-all ${!table.active ? 'opacity-50' : ''} ${statusColor[table.status] || 'border-gray-200'}`}
                        >
                            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-gray-400">№ {table.number}</div>
                            <div className="mb-0.5 font-bold text-gray-800">{table.name}</div>
                            {rooms.find(r => r.id === table.room_id)?.is_takeaway ? (
                                <div className="mb-1 text-xs font-semibold text-orange-600">🥡 Takeaway</div>
                            ) : null}
                            {table.room_name && (
                                <div className="mb-1 text-xs font-semibold text-primary-500">🏠 {table.room_name}</div>
                            )}
                            {isPro && Number(table.deposit_amount || 0) > 0 && (
                                <div className="mb-2 mt-1 text-xs font-semibold text-amber-700">💰 Depozit hədəfi: {Number(table.deposit_amount).toFixed(2)} ₼</div>
                            )}
                            <div className="mb-3 mt-2">
                                <span className={`rounded-full border px-2 py-1 text-xs font-bold ${statusColor[table.status]}`}>{statusMap[table.status]}</span>
                            </div>
                            {table.order_count > 0 && (
                                <div className="mb-2 text-xs text-gray-400">📋 {table.order_count} sifariş tarixi</div>
                            )}
                            <div className="flex flex-wrap gap-1.5">
                                <button onClick={() => openEdit(table)} className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 transition-all hover:bg-gray-200">Redaktə</button>
                                <button onClick={() => handleToggle(table)} className="rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-600 transition-all hover:bg-amber-100">{table.active ? 'Deaktiv' : 'Aktiv'}</button>
                                <button onClick={() => setDeleteCandidate(table)} className="rounded-lg bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-500 transition-all hover:bg-red-100">Sil</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── YARAT MODAL (tək + toplu) ── */}
            {createOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && setCreateOpen(false)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                        <h3 className="mb-1 text-lg font-bold text-gray-800">Yeni masa</h3>
                        <p className="mb-4 text-xs text-gray-500">Nömrə avtomatik verilir. Qrupu seç, say &gt; 1 olsa avtomatik ardıcıl yaradılır.</p>
                        <div className="flex flex-col gap-4">
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Qrup</label>
                                <PrefixCombo value={createForm.prefix} onChange={setPrefix} options={prefixList} placeholder="Masa" />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Say (neçə masa)</label>
                                <input
                                    type="number" min={1} max={200}
                                    value={createForm.count}
                                    onChange={(e) => setCreateForm({ ...createForm, count: e.target.value })}
                                    className={inputCls}
                                />
                                {parseInt(createForm.count, 10) > 1 && (
                                    <div className="mt-1 text-[11px] text-gray-400">
                                        {createForm.prefix.trim()} - {nextSuffixFor(createForm.prefix)} → {createForm.prefix.trim()} - {nextSuffixFor(createForm.prefix) + (parseInt(createForm.count, 10) || 1) - 1}
                                    </div>
                                )}
                            </div>

                            {(parseInt(createForm.count, 10) || 1) === 1 && (
                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Ad (dəyişə bilərsiniz)</label>
                                    <input
                                        type="text"
                                        value={createForm.name}
                                        onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                        placeholder="Masa - 1"
                                        className={inputCls}
                                    />
                                </div>
                            )}

                            {isPro && (
                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Depozit hədəfi (₼)</label>
                                    <input
                                        type="number" min={0} step="0.01"
                                        value={createForm.deposit_amount}
                                        onChange={(e) => setCreateForm({ ...createForm, deposit_amount: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>
                            )}

                            {rooms.length > 0 && !isFreePlan(user?.tenant_plan) && (
                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Otaq / Zal</label>
                                    <SearchSelect
                                        value={createForm.room_id}
                                        onChange={(v) => setCreateForm({ ...createForm, room_id: v })}
                                        options={rooms.map((r) => ({ value: r.id, label: `${r.is_takeaway ? '🥡 ' : ''}${r.name}` }))}
                                        placeholder="— Otaq seçilməyib —"
                                    />
                                </div>
                            )}

                            <div className="mt-2 flex gap-3">
                                <button type="button" onClick={() => setCreateOpen(false)} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50">Ləğv et</button>
                                <button
                                    type="button" onClick={submitCreate} disabled={createSaving}
                                    className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white transition-all disabled:opacity-50"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                >
                                    {createSaving ? '...' : (parseInt(createForm.count, 10) > 1 ? `${createForm.count} masa yarat` : 'Əlavə et')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── REDAKTƏ MODAL ── */}
            {editTable && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && setEditTable(null)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                        <h3 className="mb-5 text-lg font-bold text-gray-800">Masanı redaktə et</h3>
                        <form onSubmit={submitEdit} className="flex flex-col gap-4">
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Nömrə (dəyişmir)</label>
                                <input type="text" value={`№ ${editTable.number}`} disabled className="w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-400" />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Ad</label>
                                <input
                                    type="text" value={editForm.name}
                                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                    placeholder="Məs: Masa - 1, VIP - 1"
                                    className={inputCls}
                                />
                            </div>
                            {isPro && (
                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Depozit hədəfi (₼)</label>
                                    <input
                                        type="number" value={editForm.deposit_amount}
                                        onChange={(e) => setEditForm({ ...editForm, deposit_amount: e.target.value })}
                                        min="0" step="0.01" className={inputCls}
                                    />
                                    <div className="mt-1 text-[11px] font-semibold text-amber-700">Sifariş bu məbləğdən aşağı olsa, fərq depozit kimi tamamlanacaq.</div>
                                </div>
                            )}
                            {rooms.length > 0 && !isFreePlan(user?.tenant_plan) && (
                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Otaq / Zal</label>
                                    <SearchSelect
                                        value={editForm.room_id}
                                        onChange={(v) => setEditForm({ ...editForm, room_id: v })}
                                        options={rooms.map((r) => ({ value: r.id, label: `${r.is_takeaway ? '🥡 ' : ''}${r.name}` }))}
                                        placeholder="— Otaq seçilməyib —"
                                    />
                                </div>
                            )}
                            {/* Doluluq faizinə hesablansın? (QR menyuda "restoran X% doludur").
                                Zalın doluluq tiki sönükdürsə → bu tik DISABLED (zal bütün masalarını xaric edir). */}
                            {(() => {
                                const selRoom = rooms.find((r) => String(r.id) === String(editForm.room_id));
                                const roomExcluded = !!selRoom && Number(selRoom.count_occupancy) === 0;
                                return (
                                    <label className={`flex items-start gap-2 rounded-xl border p-3 ${roomExcluded ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-70' : 'border-sky-100 bg-sky-50/40 cursor-pointer'}`}>
                                        <input
                                            type="checkbox"
                                            disabled={roomExcluded}
                                            checked={!roomExcluded && editForm.count_occupancy !== false}
                                            onChange={(e) => setEditForm({ ...editForm, count_occupancy: e.target.checked })}
                                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 disabled:cursor-not-allowed"
                                        />
                                        <div className="flex-1">
                                            <div className={`text-sm font-bold ${roomExcluded ? 'text-gray-500' : 'text-sky-800'}`}>📊 Doluluq faizinə hesablansın</div>
                                            <div className={`mt-0.5 text-[11px] leading-snug ${roomExcluded ? 'text-gray-400' : 'text-sky-700/80'}`}>
                                                {roomExcluded
                                                    ? `"${selRoom.name}" zalında doluluq söndürülüb — bu masa faizə heç cür daxil olmur. Aktiv etmək üçün əvvəlcə zalın doluluq tikini açın.`
                                                    : 'QR menyuda "restoran X% doludur" göstəricisinə bu masa daxil olsun. Söndürülsə, bu masaya vurulan sifarişlər faizə təsir etmir.'}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })()}
                            <div className="mt-2 flex gap-3">
                                <button type="button" onClick={() => setEditTable(null)} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all">Ləğv et</button>
                                <button type="submit" disabled={editSaving} className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                                    {editSaving ? '...' : 'Yadda saxla'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── SIRALA MODAL ── */}
            {sortOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && setSortOpen(false)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                        <h3 className="mb-1 text-lg font-bold text-gray-800">⇅ Qrupların sırası</h3>
                        <p className="mb-4 text-xs text-gray-500">Ox düymələri ilə qrupları yuxarı/aşağı apar. Ofisiant ekranında bu sıra ilə görünəcək.</p>
                        <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
                            {groupOrder.map((p, idx) => {
                                const cnt = tables.filter((t) => splitName(t.name).prefix === p).length;
                                return (
                                    <div key={p} className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                                        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary-100 text-xs font-bold text-primary-700">{idx + 1}</span>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-bold text-gray-800">{p}</div>
                                            <div className="text-[11px] text-gray-400">{cnt} masa</div>
                                        </div>
                                        <button type="button" onClick={() => moveGroup(idx, -1)} disabled={idx === 0}
                                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-primary-400 disabled:opacity-30">▲</button>
                                        <button type="button" onClick={() => moveGroup(idx, 1)} disabled={idx === groupOrder.length - 1}
                                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-primary-400 disabled:opacity-30">▼</button>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-4 flex gap-3">
                            <button type="button" onClick={() => setSortOpen(false)} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50">Ləğv et</button>
                            <button type="button" onClick={saveSort} disabled={sortSaving} className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                                {sortSaving ? '...' : 'Yadda saxla'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteCandidate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && setDeleteCandidate(null)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                        <h3 className="mb-2 text-lg font-bold text-gray-800">Masanı sil</h3>
                        <p className="mb-5 text-sm text-gray-500">
                            <span className="font-semibold text-gray-700">{deleteCandidate.name}</span> silinsin?
                        </p>
                        <div className="flex gap-3">
                            <button type="button" onClick={() => setDeleteCandidate(null)} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50">Geri</button>
                            <button type="button" onClick={handleDelete} className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-bold text-white hover:bg-red-600">Sil</button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Tables;
